import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { Plus, Trash2, Pencil } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function AdminDashboard() {
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        const [c, s] = await Promise.all([api.get("/computers"), api.get("/computers/stats")]);
        setItems(c.data);
        setStats(s.data);
        setLoading(false);
    };

    useEffect(() => {
        load();
    }, []);

    const handleDelete = async (id, name) => {
        if (!window.confirm(`Delete "${name}" from the archive?`)) return;
        try {
            await api.delete(`/computers/${id}`);
            toast.success(`${name} removed.`);
            load();
        } catch (e) {
            toast.error("Delete failed.");
        }
    };

    const totalValue = useMemo(() => stats?.total_value || 0, [stats]);

    if (loading) {
        return (
            <div className="py-32 text-center font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground">
                Booting dashboard<span className="terminal-caret" />
            </div>
        );
    }

    return (
        <div data-testid="admin-dashboard" className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-10">
            <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border/60 pb-6">
                <div>
                    <div className="overline">// Admin.console</div>
                    <h1 className="mt-2 font-serif text-5xl">Curator&apos;s Console</h1>
                </div>
                <button
                    data-testid="admin-new-btn"
                    onClick={() => navigate("/admin/new")}
                    className="inline-flex items-center gap-2 border border-phosphor-amber bg-phosphor-amber px-5 py-3 font-mono text-[10px] uppercase tracking-[0.3em] text-background hover:bg-transparent hover:text-phosphor-amber transition-colors"
                >
                    <Plus className="h-3.5 w-3.5" />
                    New record
                </button>
            </div>

            {/* Stat tiles */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-8">
                <StatTile label="Total Records" value={stats?.total || 0} accent="amber" testid="stat-total" />
                <StatTile label="Total Value" value={`$${totalValue.toLocaleString()}`} accent="green" testid="stat-value" />
                <StatTile label="Manufacturers" value={stats?.by_brand?.length || 0} testid="stat-brands" />
                <StatTile label="Decades Covered" value={stats?.by_decade?.length || 0} testid="stat-decades" />
            </div>

            {/* Charts */}
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ChartBox title="By Decade" data={stats?.by_decade || []} color="#FFB000" testid="chart-decade" />
                <ChartBox title="By Manufacturer" data={stats?.by_brand || []} color="#39FF14" testid="chart-brand" />
            </div>

            {/* Table */}
            <div className="mt-10 border border-border">
                <div className="border-b border-border bg-muted/30 px-6 py-3 flex items-center justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-phosphor-amber">
                        &gt; RECORDS.TBL
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground">
                        {items.length} entries
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-border font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                                <th className="px-6 py-3">Name</th>
                                <th className="px-6 py-3">Manufacturer</th>
                                <th className="px-6 py-3">Year</th>
                                <th className="px-6 py-3">Condition</th>
                                <th className="px-6 py-3 text-right">Value</th>
                                <th className="px-6 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} className="border-b border-border/60 hover:bg-muted/30">
                                    <td className="px-6 py-4">
                                        <Link
                                            to={`/computer/${c.id}`}
                                            className="font-serif text-lg text-foreground hover:text-phosphor-amber"
                                        >
                                            {c.name}
                                        </Link>
                                    </td>
                                    <td className="px-6 py-4 font-mono text-xs text-muted-foreground uppercase tracking-[0.15em]">
                                        {c.manufacturer}
                                    </td>
                                    <td className="px-6 py-4 font-mono text-xs text-phosphor-amber">
                                        {c.year}
                                    </td>
                                    <td className="px-6 py-4 font-mono text-xs text-muted-foreground">
                                        <span className="text-phosphor-green">●</span> {c.condition || "—"}
                                    </td>
                                    <td className="px-6 py-4 font-mono text-xs text-right text-phosphor-green">
                                        {c.current_value ? `$${c.current_value.toLocaleString()}` : "—"}
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="inline-flex gap-2">
                                            <button
                                                data-testid={`edit-${c.id}`}
                                                onClick={() => navigate(`/admin/${c.id}/edit`)}
                                                className="border border-border p-2 hover:border-phosphor-amber hover:text-phosphor-amber transition-colors"
                                            >
                                                <Pencil className="h-3 w-3" />
                                            </button>
                                            <button
                                                data-testid={`delete-${c.id}`}
                                                onClick={() => handleDelete(c.id, c.name)}
                                                className="border border-border p-2 hover:border-destructive hover:text-destructive transition-colors"
                                            >
                                                <Trash2 className="h-3 w-3" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function StatTile({ label, value, accent, testid }) {
    const color = accent === "amber" ? "text-phosphor-amber" : accent === "green" ? "text-phosphor-green" : "text-foreground";
    return (
        <div data-testid={testid} className="border border-border bg-card p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
            <div className={`mt-3 font-serif text-4xl ${color}`}>{value}</div>
        </div>
    );
}

function ChartBox({ title, data, color, testid }) {
    return (
        <div data-testid={testid} className="border border-border bg-card p-6">
            <div className="mb-4 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-phosphor-amber">
                    &gt; {title.replace(/ /g, "_")}
                </div>
                <div className="font-mono text-[10px] text-muted-foreground">n={data.reduce((s, x) => s + x.count, 0)}</div>
            </div>
            <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data}>
                        <XAxis dataKey="label" stroke="#666" />
                        <YAxis stroke="#666" allowDecimals={false} />
                        <Tooltip
                            contentStyle={{
                                background: "#09090B",
                                border: "1px solid #27272A",
                                fontFamily: "JetBrains Mono",
                                fontSize: 12,
                                color: "#EDEDED",
                            }}
                            cursor={{ fill: "rgba(255,176,0,0.06)" }}
                        />
                        <Bar dataKey="count" fill={color}>
                            {data.map((_, i) => (
                                <Cell key={i} fill={color} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
